import { useEffect } from 'react';

import { useChannel } from 'ably/react';

import { CfsApi, UnitApi } from '@/api';
import { useLocationTracking } from '@/hooks/useLocationTracker';
import { CFSEvent, IUser, Role, UnitStatus } from '@/models';
import { isTargetRole } from '@/services';
import { useUserStore } from '@/store';

export function LocationTracker() {
  const { account, user } = useUserStore();

  function saveAssignedUnitShiftId(unitShifts: any[], userId: string): boolean {
    const found = unitShifts.find((shift) => {
      const isOnDuty = shift.unit?.status === UnitStatus.ON_DUTY;
      const userIsIncluded =
        Array.isArray(shift.users) &&
        //TODO: @Shpetim use the IUser interface
        shift.users.some((shiftUser: any) => shiftUser._id === userId);
      return isOnDuty && userIsIncluded;
    });

    if (found) {
      localStorage.setItem('assignedUnitShiftId', found._id);
      return true;
    }

    return false;
  }

  useEffect(() => {
    const fetchAndCheckAssignedUnitShift = async () => {
      if (!user?._id) return;

      const filter = JSON.stringify({ status: 'ACTIVE' });
      const unitRes = await UnitApi.getUnitShifts({ filter, limit: 1000 });
      const unitShifts = unitRes.data.results ?? [];

      const hasOnDutyShift = saveAssignedUnitShiftId(unitShifts, user._id);

      const cfsRes = await CfsApi.listByUser(user._id);
      const assignedCfsList = cfsRes.data?.assigned ?? [];

      let foundAssignedCFS = false;

      for (const cfs of assignedCfsList) {
        const assignedShift = cfs.assignedUnitShifts.find(
          (shift: any) => shift.status === 'ASSIGNED',
        );

        if (assignedShift) {
          localStorage.setItem('cfsId', cfs._id as string);
          localStorage.setItem(
            'assignedUnitShiftId',
            assignedShift.unitShift as unknown as string,
          );
          foundAssignedCFS = true;
          break;
        }
      }

      if (!foundAssignedCFS) {
        localStorage.removeItem('cfsId');
        if (!hasOnDutyShift) {
          localStorage.removeItem('assignedUnitShiftId');
        }
      }
    };

    if (user?._id && isTargetRole(user as IUser, Role.FIRST_RESPONDER)) {
      fetchAndCheckAssignedUnitShift();
    }
  }, [user]);

  useChannel(
    `account:${account?._id}:cfs:assigned-unit-shift`,
    async (message) => {
      if (!isTargetRole(user as IUser, Role.FIRST_RESPONDER)) return;

      const assignedUnitShift = message.data?.assignedUnitShifts;
      const assignedUsers = assignedUnitShift?.unitShift?.users || [];
      const status = assignedUnitShift?.status;
      const currentUserId = user?._id;

      switch (message.name) {
        case CFSEvent.UNIT_SHIFT_ASSIGNED: {
          const isUserAssigned = assignedUsers.some(
            (assignedUser: { _id: string }) =>
              assignedUser._id === currentUserId,
          );

          if (isUserAssigned && status === 'ASSIGNED') {
            const cfsId = message.extras?.headers?.cfs;
            const assignedUnitShiftId = assignedUnitShift?.unitShift?._id;

            if (cfsId && assignedUnitShiftId) {
              localStorage.setItem('cfsId', cfsId);
              localStorage.setItem('assignedUnitShiftId', assignedUnitShiftId);
            }
          }
          break;
        }

        case CFSEvent.UNIT_SHIFT_COMPLETED:
        case CFSEvent.UNIT_SHIFT_CANCELED: {
          const savedAssignedUnitShiftId = localStorage.getItem(
            'assignedUnitShiftId',
          );

          if (
            assignedUnitShift?.unitShift?._id &&
            assignedUnitShift?.unitShift?._id === savedAssignedUnitShiftId
          ) {
            localStorage.removeItem('cfsId');
            localStorage.removeItem('assignedUnitShiftId');
          }
          break;
        }
      }
    },
  );

  useChannel(`account:${account?._id}:unit-shift`, (message) => {
    if (!isTargetRole(user as IUser, Role.FIRST_RESPONDER)) return;

    const assignedId = localStorage.getItem('assignedUnitShiftId');
    const unitShiftFromMessage = message?.extras?.headers?.unitShift;

    // Handle UNASSIGNED_FROM_CALL
    if (message?.name === 'UNASSIGNED_FROM_CALL') {
      const isSameUnitShift = unitShiftFromMessage === assignedId;
      const status = message?.data?.status;

      if (isSameUnitShift) {
        if (status !== 'ACTIVE') {
          localStorage.removeItem('assignedUnitShiftId');
        }
      }
    }

    // Handle CREATED
    if (message?.name === 'CREATED') {
      const userId = user?._id;
      const users = message?.data?.users ?? [];
      const isCurrentUserAssigned = users.includes(userId);

      if (isCurrentUserAssigned && unitShiftFromMessage) {
        localStorage.setItem('assignedUnitShiftId', unitShiftFromMessage);
      }
    }

    // Handle CLOSED
    if (message?.name === 'CLOSED') {
      const isSameUnitShift = unitShiftFromMessage === assignedId;
      if (isSameUnitShift) {
        localStorage.removeItem('assignedUnitShiftId');
      }
    }
  });

  useLocationTracking();

  return null;
}
